import Icon from "@Components/generic/icon";
import { AppContext, Events } from "@Context/common/general/context";
import AuthSecurityLevel from "@Enums/AuthSecurityLevel";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { BoxProps, IconButton, Box as MuiBox, SxProps, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import React, { FC, ReactNode, useContext, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import HapticsButtonWrapper from "./haptics-button-wrapper";

interface AnimationState {
  in: AnimatorDirection | null;
  out: AnimatorDirection | null;
  transition: string;
  duration: number;
}

const MOTION_VARIANTS = {
  initial: (animationState: AnimationState) => ({
    x:
      animationState.in === AnimatorDirection.IN_LEFT
        ? "-150%"
        : animationState.in === AnimatorDirection.IN_RIGHT
        ? "150%"
        : 0,
    perspective: 1,
    transition: {
      ease: animationState.transition,
      duration: animationState.duration,
    },
  }),
  in: (animationState: AnimationState) => ({
    x: 0,
    perspective: 1,
    transition: {
      ease: animationState.transition,
      duration: animationState.duration,
    },
    transitionEnd: {
      x: 0,
    },
  }),
  out: (animationState: AnimationState) => ({
    x: animationState.out === AnimatorDirection.OUT_RIGHT ? "150%" : "-150%",
    perspective: 1,
    transition: {
      ease: animationState.transition,
      duration: animationState.duration,
    },
  }),
};

enum AnimatorDirection {
  OUT_LEFT = "OUT_LEFT",
  OUT_RIGHT = "OUT_RIGHT",
  IN_LEFT = "IN_LEFT",
  IN_RIGHT = "IN_RIGHT",
  NONE = "NONE",
}

const BoxComponent = React.forwardRef((props: BoxProps, ref) => <MuiBox {...props} ref={ref} />);
const Box = motion(BoxComponent);

/**
 * Nav Button Component
 */
const NavButton = React.memo(
  ({ visible, timeout }: { visible: boolean; timeout?: number }) => {
    const theme = useTheme();
    const [disabled, setDisabled] = useState(false);

    return (
      <MuiBox
        sx={{
          display: visible ? "flex" : "none",
          width: "fit-content",
          height: "100vh",
          position: "fixed",
          top: 0,
          alignItems: "center",
          justifyContent: "center",
          left: theme.spacing(3),
          "@keyframes fadeIn": {
            "0%": { opacity: 0 },
            "100%": { opacity: 0.8 },
          },
          "@keyframes fadeOut": {
            "0%": { opacity: 0.8 },
            "100%": { opacity: 0 },
          },
          animation: `$fadeIn linear 0.5s`,
          animationFillMode: "forwards",
          animationIterationCount: 1,
        }}
      >
        <HapticsButtonWrapper>
          <IconButton
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={() => {
              Events.app.navigateBack.emit();
              setDisabled(true);
              setTimeout(() => {
                setDisabled(false);
              }, timeout || 0); //
            }}
            disabled={disabled}
          >
            <Icon
              icon={faChevronLeft}
              sx={{
                fontSize: theme.spacing(15),
                // This component is bound to be deprecated soon, so we'll just hack the color
                color: theme.palette.mode === "dark" ? "dsNeutral.v300" : "#9EA9BB",
                opacity: disabled ? 0.5 : 1,
                "&:hover": {
                  opacity: 0.75,
                },
              }}
            />
          </IconButton>
        </HapticsButtonWrapper>
      </MuiBox>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.visible === nextProps.visible;
  }
);

interface IContainerWrapper {
  id: string;
  children: ReactNode;
  animate?: boolean;
  securityLevel?: AuthSecurityLevel;
  enableBackButton?: boolean;
  animationDelay?: number;
  fullHeight?: boolean;
  sx?: SxProps;
}

/**
 *
 * @param fullHeight If `true` the content of the container wrapper with fill the entire screen height. Useful for
 * when you want to show the header bar and have content be rendered behind it. If you want to hide the header bar
 * you should use the exclusivePageEntered event instead of this prop.
 * @returns A wrapper component for Web, Mobile and VR that handles animation, back navigation and content height
 */
const ContainerWrapper: FC<IContainerWrapper> = ({
  id,
  children,
  animate = true,
  securityLevel = AuthSecurityLevel.L3,
  enableBackButton,
  animationDelay = 0.1,
  fullHeight = false,
  sx,
}) => {
  const { state } = useContext(AppContext);
  const theme = useTheme();
  const location = useLocation();
  const appBarHeight = state.app.appBarHeight;
  const navBarHeight = state.app.navBarHeight;
  const availableRoutes = state.app.availableRoutes;
  const isMobile = state.device.isMobile;
  const animationDuration = isMobile ? 100 : 300; //ms

  const [animationState, setAnimationState] = useState<Omit<AnimationState, "transition" | "duration">>();

  const getDirectionFromPath = (newPath: string, prevPath?: string) => {
    let newPathIndex: null | number = null;
    let currentPathIndex: null | number = null;
    for (let i = 0; i < availableRoutes.length - 1; i++) {
      if (matchPath({ path: availableRoutes[i] }, newPath)) {
        newPathIndex = i;
      }
      if (matchPath({ path: availableRoutes[i] }, prevPath || location.pathname)) {
        currentPathIndex = i;
      }
      if (currentPathIndex && newPathIndex) {
        if (currentPathIndex > newPathIndex) {
          return {
            in: AnimatorDirection.IN_LEFT,
            out: AnimatorDirection.OUT_RIGHT,
          };
        } else if (currentPathIndex < newPathIndex) {
          return {
            in: AnimatorDirection.IN_RIGHT,
            out: AnimatorDirection.OUT_LEFT,
          };
        }
      }
    }
    return {
      in: AnimatorDirection.IN_RIGHT,
      out: AnimatorDirection.OUT_LEFT,
    };
  };

  const startAnimation = (path: string, onEndedCallback?: () => void) => {
    setAnimationState(getDirectionFromPath(path));
    setTimeout(() => {
      onEndedCallback?.();
    }, (!isMobile ? animationDelay : 0) * 2000);
  };

  const animatePage = ({
    path,
    onAnimateComplete = () => {},
  }: {
    path: string;
    onAnimateComplete?: ({ lastPath }: { lastPath?: string }) => void;
  }) => {
    if (!animate) {
      onAnimateComplete({ lastPath: location.pathname });
      return;
    }
    startAnimation(path, () => {
      onAnimateComplete({ lastPath: location.pathname });
    });
  };

  useEffect(() => {
    Events.app.animatePage.uniq(animatePage);

    // Immediately trigger auth security level check
    Events.auth.reAuthRequired.emit({
      securityLevel: securityLevel,
      onCancel: () => Events.app.navigateBack.emit(),
    });

    // Set animation state based on previous path
    if (location.state?.from) {
      setAnimationState(getDirectionFromPath(location.pathname, location.state.from));
    } else {
      setAnimationState({
        in: null,
        out: null,
      });
    }

    return () => {
      Events.app.animatePage.remove(animatePage);
    };
  }, []);

  return (
    <>
      {animationState && (
        <Box
          id={id}
          key={id}
          {...(animate && {
            custom: {
              ...animationState,
              transition: isMobile ? "easeIn" : "anticipate",
              duration: animationDuration / 1000,
            },
            initial: "initial",
            animate: "in",
            exit: "out",
            variants: MOTION_VARIANTS,
          })}
          sx={{
            marginTop: fullHeight ? 0 : `${navBarHeight}px`,
            height: fullHeight ? "100dvh" : `calc(100dvh - ${appBarHeight + navBarHeight}px)`,
            overflowY: "auto",
            overflowX: "hidden",
            ...(isMobile && {
              backgroundColor: theme.palette.dsSurface.v300,
            }),
            ...sx,
          }}
        >
          {children}
        </Box>
      )}
      <NavButton visible={!!enableBackButton} timeout={animationDuration * 2} />
    </>
  );
};

export default ContainerWrapper;
